/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
/* eslint no-loop-func: 0 */

import { Divider, Popover } from "@mui/material";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import NestedMenuItem from "@CubeComponents/html/NestedMenuItem";
import { useEffect, useRef, useState, forwardRef, useContext, useId, useImperativeHandle, useCallback } from "react";
import ColorPickerComponent from "@CubeComponents/Mui_Form/ColorPickerComponent";
import IsolateZoomHelper from "./IsolateZoomHelper.js";
import type { IsolateZoomHelperConstructor } from "./IsolateZoomHelper.js";
import { setModalStatus, updateTransparentElements, setViewerState } from "@Reducers/Viewer";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { getAllLeafNodesRecur, CubeGlobal } from "../HCViewerUtils.js";
import _ from "lodash";
import useViewerCallbacks from "../useViewerCallbacks.jsx";
import { useTranslation, untranslated } from "@i18n";
import { RootState } from "@Reducers/index";
import { RC_MENU_ITEMS, streamingModeMap } from "@Helpers/constants.js";
import { HoopsViewerContext } from "../ViewerContext";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers.js";
import { dispatchModelLoadingToast } from "@Actions/Viewer.js";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants.js";
import Text from "@CubeComponents/html/Text/Text.js";
import {
  Color,
  Selection,
  PickConfig,
  SelectionMask,
  NodeType,
  ElementType,
  OperatorId,
  KeyModifiers,
} from "@ts3d-hoops/web-viewer";
import IconComponent from "@CubeComponents/icons/index.js";
import type { IModel, NodeId, Point2, Point3 } from "@ts3d-hoops/web-viewer";
import type { ModalEntry } from "@Reducers/Viewer";
import type { TranslatedString } from "@i18n";
import type { ColorPickerProps, ColorPickerRef } from "@CubeComponents/Mui_Form/ColorPickerComponent";

/** One entry in the context menu; `items` makes it a nested submenu and `separator` a rule. */
interface ContextMenuItem {
  id?: string;
  text?: TranslatedString;
  iconName?: string;
  separator?: boolean;
  items?: ContextMenuItem[];
}

/** The prototype-built `IsolateZoomHelper` instance, as this menu drives it. */
interface IsolateZoomHelperHandle {
  isolateNodes(nodeIds: NodeId[], initiallyHiddenStayHidden?: boolean | null): Promise<void>;
  fitNodes(nodeIds: NodeId[]): Promise<void>;
  showAll(): Promise<void>;
}

interface HoopsRightClickContextMenuProps {
  /**
   * Accepted because all four call sites pass it, but unused: the component reads `viewerImpl`
   * from `HoopsViewerContext` instead. Left `unknown` so the callers outside this module keep
   * passing their own shapes.
   */
  viewerImpl?: unknown;
  /** Same shape as the `HoopsModalStatus` redux slice: dock panel id -> open/stage state. */
  HoopsModalStatus: Record<string, ModalEntry>;
  modelTreeNodeId?: NodeId | null;
  isOpenedThrough3DModel?: boolean;
}

const HoopsRightClickContextMenu = forwardRef(
  ({ HoopsModalStatus, modelTreeNodeId, isOpenedThrough3DModel = true }: HoopsRightClickContextMenuProps, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const settingsData = useSelector((state: RootState) => state.GetSettingModalData);
    const transparentElements = useSelector((state: RootState) => state.transparentElements);
    const viewerState = useSelector((state: RootState) => state.viewerState);
    const menuId = useId();
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
    // Widened to include `undefined` for the `item.id` lookups in `renderMenuItem`: a separator
    // entry carries no id, and `has(undefined)` returning false is the intent.
    const [disabledItemIds, setDisabledItemIds] = useState<Set<string | undefined>>(new Set());

    const openMenu = useCallback((top: number, left: number) => {
      setMenuPosition({ top, left });
    }, []);

    const closeMenu = useCallback(() => {
      setMenuPosition(null);
    }, []);

    const enableMenuItems = useCallback((ids: string[], enabled: boolean) => {
      setDisabledItemIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) {
          if (enabled) next.delete(id);
          else next.add(id);
        }
        return next;
      });
    }, []);

    useImperativeHandle(ref, () => ({
      open: openMenu,
      enableItems: enableMenuItems,
    }));
    const [selectedNodeId, setSelectedNodeId] = useState<NodeId | null>(null);
    const isolateZoomHelper = useRef<IsolateZoomHelperHandle | null>(null);
    CubeGlobal.isolateZoomHelper = isolateZoomHelper;
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [colorPickerPosition, setColorPickerPosition] = useState<{ top: number; left: number } | null>(null);
    // Seeded with `0` but only ever holds the canvas point HOOPS reports on `contextMenu`; the
    // union keeps the initializer verbatim.
    const [rightClickPosition, setRightClickPosition] = useState<Point2 | number>(0);
    const colorPickerRef = useRef<ColorPickerRef>(null);
    const { viewerImpl, isModelStructureReady, isSettingsApplied } = useContext(HoopsViewerContext);
    const loadedNodesRef = useRef(new Set());

    const selectionItems = ["Isolate", "Focus", "Hide", "Override"];
    const streamingMode = settingsData.ModelLoading_progressiveLoading;
    const hoopsCallback = {
      contextMenu(position: Point2) {
        if (
          !getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })
        ) {
          dispatch(dispatchModelLoadingToast);
          return;
        }
        setRightClickPosition(position);
        const canvasEl = document.getElementById("viewerContainer");
        const rect = canvasEl?.getBoundingClientRect();
        const offsetX = rect?.left ?? 0;
        const offsetY = rect?.top ?? 0;
        openMenu(position.y + offsetY, position.x + offsetX);
      },
    };

    const viewerCallbacks = isOpenedThrough3DModel ? hoopsCallback : null;

    useViewerCallbacks({
      viewerCallbacks,
      triggerRerender: isOpenedThrough3DModel,
    });

    useEffect(() => {
      if (
        getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }) &&
        viewerImpl?.hwv
      ) {
        const selItems = viewerImpl?.hwv.selectionManager.getResults();
        enableItems(selItems.length);
      }
    }, [rightClickPosition, isModelStructureReady, isSettingsApplied]);
    const toggle = () => {
      if (menuPosition) {
        setColorPickerPosition({ top: menuPosition.top, left: menuPosition.left });
      }
      setShowColorPicker(true);
    };

    const [nodeColor, setnodeColor] = useState("#0000FF");

    const compColorChange = function (color: Parameters<NonNullable<ColorPickerProps["change"]>>[0]) {
      setnodeColor(color.value);
    };

    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? new Color(Number.parseInt(result[1]!, 16), Number.parseInt(result[2]!, 16), Number.parseInt(result[3]!, 16))
        : null;
    };

    const shouldIncludeNode = (nodeId: NodeId | null, rootId: NodeId, includeRoot: boolean): boolean => {
      return includeRoot || nodeId !== rootId;
    };

    const isValidLoadedNode = (nodeId: NodeId, model: IModel, rootId: NodeId, includeRoot: boolean): boolean => {
      return model.isNodeLoaded(nodeId) && shouldIncludeNode(nodeId, rootId, includeRoot);
    };

    const getSelectedItemIds = (
      selectionManager: Selection.SelectionManager,
      model: IModel,
      rootId: NodeId,
      includeRoot: boolean,
    ): NodeId[] => {
      const selectedItems = selectionManager.getResults();
      const itemIds: NodeId[] = [];

      for (const item of selectedItems) {
        const id = item.getNodeId();
        if (isValidLoadedNode(id, model, rootId, includeRoot)) {
          itemIds.push(id);
        }
      }

      return itemIds;
    };

    const shouldAddClickedNode = (
      nodeId: NodeId,
      rootId: NodeId,
      includeRoot: boolean,
      itemIds: NodeId[],
      selectionManager: Selection.SelectionManager,
    ): boolean => {
      if (!shouldIncludeNode(nodeId, rootId, includeRoot)) {
        return false;
      }

      if (includeRoot) {
        return true;
      }

      if (itemIds.length === 0) {
        return true;
      }

      const selectionItem = Selection.SelectionItem.create(nodeId);
      const containsParent = selectionManager.containsParent(selectionItem) !== null;
      const containsItem = itemIds.includes(nodeId);

      return !containsItem && !containsParent;
    };

    const processClickedNode = (
      nodeId: NodeId | null,
      includeClicked: boolean,
      rootId: NodeId,
      includeRoot: boolean,
      itemIds: NodeId[],
      selectionManager: Selection.SelectionManager,
    ): void => {
      if (nodeId === null || !includeClicked) {
        return;
      }

      if (shouldAddClickedNode(nodeId, rootId, includeRoot, itemIds, selectionManager)) {
        itemIds.push(nodeId);
      }
    };

    const getContextItemIds = (
      nodeId: NodeId | null,
      includeSelected: boolean,
      includeClicked: boolean,
      includeRoot = true,
    ) => {
      if (
        !getIsViewerDependencyLoaded({ viewerImpl, hwv: CubeGlobal.hwv, isModelStructureReady, isSettingsApplied }) ||
        !viewerImpl?.hwv
      ) {
        return [];
      }

      const hwvInstance = viewerImpl.hwv;
      const { selectionManager } = hwvInstance;
      const { model } = hwvInstance;
      const rootId = model.getAbsoluteRootNode();

      let itemIds: NodeId[] = [];

      if (includeSelected) {
        itemIds = getSelectedItemIds(selectionManager, model, rootId, includeRoot);
      }

      processClickedNode(nodeId, includeClicked, rootId, includeRoot, itemIds, selectionManager);

      return itemIds;
    };

    const getModelNodeId = async (position: Point2) => {
      if (viewerImpl?.hwv) {
        let nodeId: NodeId | null = null;
        let positino3d: Point3 | null = null;

        if (!position) return { nodeId, positino3d };

        const config = new PickConfig(SelectionMask.All);
        const selectionItem = await viewerImpl?.hwv.view.pickFromPoint(position, config);
        const axisOverlay = 1;

        let nodeType;
        if (selectionItem.isNodeSelection()) {
          nodeType = viewerImpl?.hwv.model.getNodeType(selectionItem.getNodeId());
        }

        if (
          nodeType === undefined ||
          nodeType === NodeType.Pmi ||
          nodeType === NodeType.PmiBody ||
          selectionItem.overlayIndex() === axisOverlay
        ) {
          return { nodeId, positino3d };
        }

        nodeId = selectionItem.getNodeId();
        positino3d = selectionItem.getPosition();

        return { nodeId, positino3d };
      }
    };

    const isAllIfcSpace = (nodeIds: NodeId[]) => {
      if (!Array.isArray(nodeIds)) return false;
      if (viewerImpl?.hwv) {
        return nodeIds.every((nodeId: NodeId) => viewerImpl?.hwv.model.hasEffectiveGenericType(nodeId, "IfcSpace"));
      }
      return false;
    };

    const isolateFunc = async (nodeId: NodeId | null) => {
      const nodeIds = getContextItemIds(nodeId, true, true);
      await isolateNodes(nodeIds);
    };

    const isolateNodes = async (nodeIds: NodeId[] | NodeId | null) => {
      const ids = Array.isArray(nodeIds) ? nodeIds : nodeIds == null ? [] : [nodeIds];
      await isolateZoomHelper.current?.isolateNodes(ids, isAllIfcSpace(ids) ? false : null);
    };

    CubeGlobal.isolateNodes = isolateNodes;

    const zoomFunc = async (nodeId: NodeId | null) => {
      const nodeIds = getContextItemIds(nodeId, true, true);
      if (nodeIds?.length == 0) return;
      await isolateZoomHelper.current?.fitNodes(nodeIds);
    };

    CubeGlobal.zoomFunc = zoomFunc;

    const transparentFunc = async (nodeId: NodeId | null) => {
      const contextItemIds = getContextItemIds(nodeId, true, true);
      if (contextItemIds?.length == 0) return;
      const leafNodes: NodeId[] = [];

      for (const item of contextItemIds) {
        getAllLeafNodesRecur(item, leafNodes);
      }

      if (!viewerImpl?.hwv) {
        return;
      }

      // KNOWN BUG, preserved verbatim: `leafNodes` can be empty (a context item with no leaves),
      // and the first entry is read regardless. Erased assertion only.
      const opacityOfFirstItem = (await viewerImpl.hwv.model.getNodesOpacity([leafNodes[0]!]))[0];

      const tempElement = new Set(transparentElements);

      if (opacityOfFirstItem === null || opacityOfFirstItem === 1) {
        viewerImpl?.hwv.model.setNodesOpacity(leafNodes, 1 - Number(settingsData.General_transparency));
        for (const node of leafNodes) tempElement.add(node);
      } else {
        viewerImpl?.hwv.model.resetNodesOpacity(leafNodes);
        for (const node of leafNodes) tempElement.delete(node);
      }
      dispatch(updateTransparentElements(Array.from(tempElement)));
    };

    const showAllFunc = async () => {
      // KNOWN BUG, preserved verbatim: `isolateZoomHelper.current` is dereferenced with no guard
      // here, though every sibling handler uses `?.`. Erased assertion.
      await isolateZoomHelper.current!.showAll();
    };

    const _isColorSet = async (contextItemIds: NodeId[]) => {
      const activeColor = hexToRgb(nodeColor);

      for (const contextItemId of contextItemIds) {
        // DWG entities may be pure faces (e.g. text glyphs) or pure lines (e.g. polylines),
        // so consider a node "coloured" if either its faces or its lines carry an override.
        const faceColorMap = await viewerImpl?.hwv.model.getNodeColorMap(contextItemId, ElementType.Faces);
        const lineColorMap = await viewerImpl?.hwv.model.getNodeColorMap(contextItemId, ElementType.Lines);

        if (faceColorMap.size === 0 && lineColorMap.size === 0) {
          return false;
        }
        for (const color of [...faceColorMap.values(), ...lineColorMap.values()]) {
          if (!color.equals(activeColor)) {
            return false;
          }
        }
      }

      return true;
    };

    const setColorFunc = async (nodeId: NodeId | null, nodeColor: string) => {
      const contextItemIds = getContextItemIds(nodeId, true, true, false);
      if (contextItemIds.length > 0) {
        if (await _isColorSet(contextItemIds)) {
          // Reset faces, lines and points together via the material API. The legacy
          // unset*Color path did not persist the face override across the redraw that
          // HOOPS triggers on deselect for 2D drawing model space (lines survived, faces
          // did not), leaving the two out of sync.
          viewerImpl?.hwv.model.resetNodesMaterial(contextItemIds, ["faceColor", "lineColor", "pointColor"]);
        } else {
          const baseColor = Color.black();
          // KNOWN BUG, preserved verbatim: `hexToRgb` returns null for a malformed hex string and
          // the result is dereferenced regardless. Erased assertion only.
          const color = hexToRgb(nodeColor)!;
          baseColor.r = color.r;
          baseColor.g = color.g;
          baseColor.b = color.b;
          // Apply to faces, lines and points in one material override so both text (faces)
          // and drawing entities (lines) are recoloured and persist consistently.
          viewerImpl?.hwv.model.setNodesMaterial(contextItemIds, {
            faceColor: baseColor,
            lineColor: baseColor,
            pointColor: baseColor,
          });
        }
      }
    };

    CubeGlobal.setColorFunc = setColorFunc;

    const resetFunc = async () => {
      const handleOperator = viewerImpl?.hwv.operatorManager.getOperator(OperatorId.Handle);
      await handleOperator.removeHandles();
      await viewerImpl?.hwv.model.reset();
      const rootNode = viewerImpl?.hwv.model.getAbsoluteRootNode();
      viewerImpl?.hwv.model.resetNodesMaterial([rootNode], ["faceColor", "lineColor", "pointColor"]);
      dispatch(updateTransparentElements([]));
    };

    const handlesFunc = async (nodeId: NodeId | null, position: Point3 | null) => {
      const modifiers = KeyModifiers.None;
      const handleOperator = viewerImpl?.hwv.operatorManager.getOperator(OperatorId.Handle);
      const contextItemIds = getContextItemIds(nodeId, true, true, false);
      if (contextItemIds.length > 0) {
        try {
          // KNOWN BUG, preserved verbatim: `modifiers` is the constant `KeyModifiers.None`, so
          // this comparison is always false and the Shift branch is dead. Erased widening only.
          await handleOperator.addHandles(
            contextItemIds,
            (modifiers as KeyModifiers) === KeyModifiers.Shift ? null : position,
          );
        } catch (e: unknown) {
          if (
            (e as Error).message == "Handles are not enabled when the model is exploded" ||
            (e as Error).message == "Handles are not enabled while a measurement is in progress."
          ) {
            dispatch(
              showToastNotification({
                content: untranslated((e as Error).message),
                type: TOAST_TYPE_WARNING,
              }),
            );
          } else {
            throw e;
          }
        }
      }
    };

    const scaleHandleFunction = async (nodeId: NodeId | null) => {
      const scaleHandleOperator = viewerImpl?.hwv.view.operatorManager.getOperator(
        CubeGlobal.OperatorId.ScaleHandlesOperatorID,
      );
      const contextItemIds = getContextItemIds(nodeId, true, true, false);
      if (contextItemIds.length <= 0) return;
      scaleHandleOperator.show(contextItemIds[0]);
    };

    const _isItemVisible = async (nodeId: NodeId | null) => {
      if (nodeId === null) {
        const selectionItems = viewerImpl?.hwv.selectionManager.getResults();
        if (selectionItems.length === 0) {
          return false;
        }
        nodeId = selectionItems[0].getNodeId();
      }

      return viewerImpl?.hwv.model.getNodeVisibility(nodeId);
    };

    const _isMenuItemVisible = async (nodeId: NodeId | null) => _isItemVisible(nodeId);

    const visibilityFunc = async (nodeId: NodeId | null) => {
      const visible = await _isMenuItemVisible(nodeId);
      const nodeIds = getContextItemIds(nodeId, true, true);
      await viewerImpl?.hwv.model.setNodesVisibility(nodeIds, !visible, isAllIfcSpace(nodeIds)! ? false : null);
      viewerImpl?.hwv.selectionManager.clear();
    };

    const onDemandLoad = async (modelTreeNodeId: NodeId) => {
      await viewerImpl?.hwv.model.requestNodes([modelTreeNodeId]);
      loadedNodesRef.current.add(modelTreeNodeId);
    };

    const getFileNode = (nodeId: NodeId | null) => {
      if (!nodeId) return;

      const absoluteRoot = viewerImpl?.hwv.model.getAbsoluteRootNode();
      let rootNode: NodeId | null = null;
      let currentNode = nodeId;

      while (rootNode == null) {
        const parent = viewerImpl?.hwv.model.getNodeParent(currentNode);
        if (parent == null) break;
        if (parent == absoluteRoot) {
          rootNode = currentNode;
          break;
        }
        currentNode = parent;
      }

      return rootNode;
    };

    const selectFileFunc = (nodeId: NodeId | null) => {
      if (!nodeId) return;

      const fileNode = getFileNode(nodeId);

      if (fileNode != null) {
        viewerImpl?.hwv.selectionManager.clear();
        viewerImpl?.hwv.selectionManager.selectNode(fileNode);
      }
    };

    useEffect(() => {
      if (viewerImpl?.hwv) {
        isolateZoomHelper.current = new (IsolateZoomHelper as typeof IsolateZoomHelper & IsolateZoomHelperConstructor)(
          viewerImpl?.hwv,
        );
      }
    }, [viewerImpl?.hwv]);

    const allMenuItems: ContextMenuItem[] = [
      {
        id: RC_MENU_ITEMS.ISOLATE,
        text: t("Isolate"),
        iconName: "Isolate",
      },
      {
        id: RC_MENU_ITEMS.FOCUS,
        text: t("Focus"),
        iconName: "Highlight",
      },
      {
        id: RC_MENU_ITEMS.HIDE,
        text: t("Hide"),
        iconName: "EyeClose",
      },
      {
        id: RC_MENU_ITEMS.ON_DEMAND_LOAD,
        text: t("Load Node"),
      },
      {
        separator: true,
      },
      {
        id: RC_MENU_ITEMS.OVERRIDE,
        text: t("Override"),
        iconName: "Override",
        items: [
          {
            id: RC_MENU_ITEMS.TRANSFORM,
            text: t("Transform"),
            iconName: "Transform",
          },
          {
            id: RC_MENU_ITEMS.SCALE,
            text: t("Scale"),
            iconName: "Scale",
          },
          {
            id: RC_MENU_ITEMS.SET_COLOR,
            text: t("Set Color"),
            iconName: "SetColor",
          },
          {
            id: RC_MENU_ITEMS.MODIFY_COLOR,
            text: t("Modify Color"),
            iconName: "modifyColor",
          },
          {
            id: RC_MENU_ITEMS.TRANSPARENCY,
            text: t("Transparency"),
            iconName: "Transparency",
          },
        ],
      },
      {
        id: RC_MENU_ITEMS.SELECTION,
        text: t("Selection"),
        iconName: "Selection",
        items: [
          {
            id: RC_MENU_ITEMS.FILE,
            text: t("File"),
            iconName: "files",
          },
          {
            separator: true,
          },
          {
            id: RC_MENU_ITEMS.ELEMENT,
            text: t("Element"),
            iconName: viewerState.selectionMode == "element" ? "Highlight" : "",
          },
          {
            id: RC_MENU_ITEMS.PARENT,
            text: t("Parent"),
            iconName: viewerState.selectionMode == "parent" ? "Highlight" : "",
          },
          {
            id: RC_MENU_ITEMS.GRAND_PARENT,
            text: t("GrandParent"),
            iconName: viewerState.selectionMode == "grandParent" ? "Highlight" : "",
          },
        ],
      },
      {
        id: RC_MENU_ITEMS.SHOW_ALL,
        text: t("Show All"),
        iconName: "ShowAll",
      },
      {
        id: RC_MENU_ITEMS.PROPERTIES,
        text: t("Properties"),
        iconName: "Properties_icon",
      },
      {
        id: RC_MENU_ITEMS.RESET,
        text: t("Reset"),
        iconName: "Reset_icon",
      },
    ];
    const removeLoadNode = (arr: ContextMenuItem[]) =>
      arr.filter((item: ContextMenuItem) => item.id !== RC_MENU_ITEMS.ON_DEMAND_LOAD);

    const menuItems = streamingMode === streamingModeMap.OnDemand ? allMenuItems : removeLoadNode(allMenuItems);

    const handleMenuItemClick = (item: ContextMenuItem) => {
      onSelect({ item });
      closeMenu();
    };

    // Widened to include `undefined` so the `args.item.id` lookup below stays verbatim: a
    // separator entry has no id, and `has(undefined)` returning false is the intent.
    const VIEWER_DEPENDENT_ITEMS = new Set<string | undefined>([
      RC_MENU_ITEMS.ISOLATE,
      RC_MENU_ITEMS.FOCUS,
      RC_MENU_ITEMS.HIDE,
      RC_MENU_ITEMS.ON_DEMAND_LOAD,
      RC_MENU_ITEMS.TRANSFORM,
      RC_MENU_ITEMS.SCALE,
      RC_MENU_ITEMS.SET_COLOR,
      RC_MENU_ITEMS.TRANSPARENCY,
      RC_MENU_ITEMS.RESET,
      RC_MENU_ITEMS.PROPERTIES,
      RC_MENU_ITEMS.SHOW_ALL,
      RC_MENU_ITEMS.FILE,
    ]);

    const onSelect = async (args: { item: ContextMenuItem }) => {
      const nodeId = selectedNodeId;

      if (
        VIEWER_DEPENDENT_ITEMS.has(args.item.id) &&
        !getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })
      ) {
        dispatch(dispatchModelLoadingToast);
        return;
      }

      switch (args.item.id) {
        case RC_MENU_ITEMS.ISOLATE:
          isolateFunc(nodeId);
          break;
        case RC_MENU_ITEMS.FOCUS:
          zoomFunc(nodeId);
          break;
        case RC_MENU_ITEMS.HIDE:
          visibilityFunc(nodeId);
          break;
        case RC_MENU_ITEMS.ON_DEMAND_LOAD:
          // Only reachable in on-demand streaming mode, where ModelTree always supplies the id.
          await onDemandLoad(modelTreeNodeId!);
          break;
        case RC_MENU_ITEMS.TRANSFORM:
          handlesFunc(nodeId, null);
          break;
        case RC_MENU_ITEMS.SCALE:
          scaleHandleFunction(nodeId);
          break;
        case RC_MENU_ITEMS.SET_COLOR:
          setColorFunc(nodeId, nodeColor);
          break;
        case RC_MENU_ITEMS.MODIFY_COLOR:
          toggle();
          break;
        case RC_MENU_ITEMS.TRANSPARENCY:
          transparentFunc(nodeId);
          break;
        case RC_MENU_ITEMS.RESET:
          resetFunc();
          break;
        case RC_MENU_ITEMS.PROPERTIES: {
          const selection = viewerImpl?.hwv.selectionManager.getResults();
          if (nodeId && selection.length == 0) {
            viewerImpl?.hwv.selectionManager.selectNode(nodeId);
          }
          dispatch(
            setModalStatus({
              ...HoopsModalStatus,
              Metadata: {
                show: true,
              },
            }),
          );
          break;
        }
        case RC_MENU_ITEMS.SHOW_ALL:
          showAllFunc();
          break;
        case RC_MENU_ITEMS.FILE:
          selectFileFunc(nodeId);
          break;
        case RC_MENU_ITEMS.ELEMENT:
          dispatch(setViewerState({ selectionMode: "element" }));
          break;
        case RC_MENU_ITEMS.PARENT:
          dispatch(setViewerState({ selectionMode: "parent" }));
          break;
        case RC_MENU_ITEMS.GRAND_PARENT:
          dispatch(setViewerState({ selectionMode: "grandParent" }));
          break;
        default:
          break;
      }
    };

    const getAllMenuIds = (items: ContextMenuItem[]): string[] =>
      items.flatMap((item: ContextMenuItem) =>
        item.separator ? [] : [...(item.id ? [item.id] : []), ...(item.items ? getAllMenuIds(item.items) : [])],
      );

    // `enable` is a boolean at every call but one, where the selection *count* is passed and
    // relied on for truthiness — hence the union and the assertion below.
    const enableItems = (enable: boolean | number, nodeId?: NodeId | null, isNodeLoaded?: boolean) => {
      const allMenuIds = getAllMenuIds(allMenuItems);

      if (_.get(HoopsModalStatus, "ModelCompare.show")) {
        enableMenuItems(allMenuIds, false);
        return;
      }

      if (streamingMode === streamingModeMap.OnDemand && nodeId !== undefined) {
        handleOnDemandMode(allMenuIds, isNodeLoaded);
      } else {
        enableMenuItems(enable ? allMenuIds : selectionItems, enable as boolean);
      }
      enableMenuItems(["Properties"], !HoopsModalStatus?.Metadata?.show);
    };

    const handleOnDemandMode = (allMenuIds: string[], isNodeLoaded?: boolean) => {
      const nodeLoaded = isNodeLoaded ?? loadedNodesRef.current.has(modelTreeNodeId);
      enableMenuItems(allMenuIds, false);

      if (nodeLoaded) {
        const itemsToEnable = allMenuIds.filter((id: string) => id !== RC_MENU_ITEMS.ON_DEMAND_LOAD);
        enableMenuItems(itemsToEnable, true);
      } else {
        enableMenuItems([RC_MENU_ITEMS.ON_DEMAND_LOAD], true);
      }
    };

    const beforeOpenEvent = async () => {
      if (
        getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }) &&
        viewerImpl?.hwv
      ) {
        const selectedNodeIdObject = await getModelNodeId(rightClickPosition as Point2);
        const nodeId = selectedNodeIdObject!.nodeId;
        const selItem = Selection.SelectionItem.create(nodeId);
        const selResults = viewerImpl?.hwv.selectionManager.getResults();
        selResults.push(selItem);
        viewerImpl?.hwv?.selectionManager.set(selResults);
        setSelectedNodeId(nodeId);

        if (streamingMode === streamingModeMap.OnDemand) {
          const isNodeLoaded = loadedNodesRef.current.has(modelTreeNodeId);
          enableItems(true, nodeId, isNodeLoaded);
        } else {
          enableItems(true);
        }
      } else {
        dispatch(dispatchModelLoadingToast);
      }
    };

    useEffect(() => {
      if (menuPosition) {
        beforeOpenEvent();
        const suppressBrowserMenu = (e: MouseEvent) => e.preventDefault();
        document.addEventListener("contextmenu", suppressBrowserMenu, true);
        return () => document.removeEventListener("contextmenu", suppressBrowserMenu, true);
      }
    }, [menuPosition]);

    const beforeCloseColorPicker = () => {
      setShowColorPicker(false);
    };

    const renderMenuItem = (item: ContextMenuItem) => {
      if (item.separator) {
        return <Divider key={`sep-${Math.random()}`} />;
      }
      const icon = <IconComponent name={item.iconName} width="1rem" height="1rem" />;
      if (item.items) {
        return (
          <NestedMenuItem
            key={item.id}
            label={item.text}
            icon={icon}
            disabled={disabledItemIds.has(item.id)}
            parentMenuOpen={!!menuPosition}
          >
            {item.items.map((subItem: ContextMenuItem) => renderMenuItem(subItem))}
          </NestedMenuItem>
        );
      }
      return (
        <MenuItemComponent
          key={item.id}
          disabled={disabledItemIds.has(item.id)}
          onClick={() => handleMenuItemClick(item)}
        >
          {icon && <span style={{ display: "flex", alignItems: "center", width: "1.25rem" }}>{icon}</span>}
          {item.text}
        </MenuItemComponent>
      );
    };

    return (
      <Text>
        <Popover
          open={showColorPicker}
          onClose={beforeCloseColorPicker}
          anchorReference="anchorPosition"
          anchorPosition={colorPickerPosition ?? undefined}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <ColorPickerComponent
            beforeClose={beforeCloseColorPicker}
            ref={colorPickerRef}
            id="comp-model-picker-2"
            enableOpacity={false}
            value={nodeColor}
            change={compColorChange}
          />
        </Popover>
        <div>
          <MenuComponent
            data-testid="rightClickMenuItems"
            id={`contextmenu_${menuId}`}
            open={!!menuPosition}
            onClose={closeMenu}
            anchorReference="anchorPosition"
            anchorPosition={menuPosition || undefined}
          >
            {menuItems.map((item: ContextMenuItem) => renderMenuItem(item))}
          </MenuComponent>
        </div>
      </Text>
    );
  },
);
HoopsRightClickContextMenu.displayName = "HoopsRightClickContextMenu";
export default HoopsRightClickContextMenu;
